import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { requireWorkspaceId } from '../shared/workspace';
import type { QueryConfig } from '../shared/react-query-types';
import { appKeys, type App, type AppUsage } from './entity';

const STALE_TIME = 60_000;

/**
 * The `apps` field is typed as a required array, but the handler's Go struct
 * carries no `omitempty`, so a nil slice would reach us as `null`. Normalise
 * here rather than asking every reader to guard the array this signature
 * already promises.
 *
 * @public
 */
export async function getApps(workspaceId: number): Promise<App[]> {
  const data = await hydraUiApi.apps.list({ params: { path: { environment_id: String(workspaceId) } } });
  return data.apps ?? [];
}

/**
 * Always-fresh: the settings list is the only view of the workspace's apps, and
 * every create, rename, default switch and delete writes through it, so each
 * entry re-reads rather than painting a list the last write may have
 * invalidated.
 *
 * @public
 */
export const getAppsQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: appKeys.list(workspaceId),
    queryFn: () => getApps(workspaceId),
    staleTime: 0,
  });

interface UseAppsOptions {
  queryConfig?: QueryConfig<typeof getAppsQueryOptions>;
}

/**
 * Non-suspense: the page renders its header immediately and shows a loading
 * table body while this resolves, and a failed read becomes the table's own
 * error state rather than unmounting the page.
 *
 * @public
 */
export const useApps = ({ queryConfig }: UseAppsOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getAppsQueryOptions(workspaceId), ...queryConfig });
};

/**
 * The same list for the readers that only need an app's name, default flag or
 * platform identifiers, fetched only when `enabled` (the `multi_app` flag gates
 * the read) and a workspace id is in scope. Mirrors `useDomainsOptional`: a
 * non-throwing workspace id plus a `skipToken` sentinel key, so a caller that
 * mounts before the workspace settles fetches nothing rather than throwing.
 *
 * Shares the list slot with {@link useApps}, so the two never disagree about
 * what the workspace holds; the longer `staleTime` here is this caller's own,
 * and keeps a supplementary read from refetching on every mount.
 *
 * @public
 */
export function useAppsOptional({ enabled = true }: { enabled?: boolean } = {}) {
  const rawWorkspaceId = useWorkspaceIdFromCurrentUrl();
  const workspaceId = rawWorkspaceId != null && rawWorkspaceId > 0 ? rawWorkspaceId : null;
  return useQuery({
    queryKey: enabled && workspaceId != null ? appKeys.list(workspaceId) : appKeys.noWorkspace(),
    queryFn: enabled && workspaceId != null ? () => getApps(workspaceId) : skipToken,
    staleTime: STALE_TIME,
    retry: false,
  });
}

/** @public */
export async function getApp(workspaceId: number, appId: number): Promise<App> {
  const data = await hydraUiApi.apps.get({
    params: { path: { environment_id: String(workspaceId), app_id: String(appId) } },
  });
  return data.app;
}

/**
 * One app, always re-read on entry: the editor loads the app's platforms and
 * credential links to build its form, and those change from the list page's own
 * actions as well as from this form's saves.
 *
 * @public
 */
export const getAppQueryOptions = (workspaceId: number, appId: number) =>
  queryOptions({
    queryKey: appKeys.detail(workspaceId, appId),
    queryFn: () => getApp(workspaceId, appId),
    staleTime: 0,
  });

interface UseAppOptions {
  queryConfig?: QueryConfig<typeof getAppQueryOptions>;
}

/**
 * Non-suspense, for the same reason as {@link useApps}: the editor owns its
 * loading and error states.
 *
 * @public
 */
export const useApp = (appId: number, { queryConfig }: UseAppOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getAppQueryOptions(workspaceId, appId), ...queryConfig });
};

/** @public */
export async function getAppUsage(workspaceId: number, appId: number): Promise<AppUsage> {
  const data = await hydraUiApi.apps.getUsage({
    params: { path: { environment_id: String(workspaceId), app_id: String(appId) } },
  });
  return data.usage;
}

/**
 * What still sends through an app. The delete flow reads it to choose between
 * the plain confirmation and the blocked-deletion notice, so it is always read
 * fresh: a count from an earlier visit would decide that on evidence nobody
 * re-checked.
 *
 * @public
 */
export const getAppUsageQueryOptions = (workspaceId: number, appId: number) =>
  queryOptions({
    queryKey: appKeys.usage(workspaceId, appId),
    queryFn: () => getAppUsage(workspaceId, appId),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
